import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, KeyRound } from "lucide-react";

/**
 * AdminAirlineAccountCodes
 * Negotiated private-fare account codes injected into Travelport
 * LowFareSearchReq, AirPriceReq and AirCreateReservationReq under
 * `<air:AccountCodes>` + `<air:ContractCodes>`. Matching engine (Phase 2)
 * selects the most specific active record at search time.
 */
const STATUS_COLORS = {
  Active: "bg-emerald-50 text-emerald-700",
  Scheduled: "bg-amber-50 text-amber-700",
  Expired: "bg-rose-50 text-rose-700",
};

export default function AdminAirlineAccountCodes() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/airline-account-codes"); setRows(data.items || []); }
    catch (_e) { toast.error("Failed to load account codes"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit?.airline_code || edit.airline_code.length < 2) { toast.error("IATA airline code required"); return; }
    if (!edit?.code) { toast.error("Account code required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/airline-account-codes", {
        airline_code: edit.airline_code.toUpperCase(),
        code: edit.code.toUpperCase(),
        contract_code: edit.contract_code?.toUpperCase() || null,
        pseudo_city_code: edit.pseudo_city_code?.toUpperCase() || null,
        booking_period_from: edit.booking_period_from || null,
        booking_period_to: edit.booking_period_to || null,
        travel_period_from: edit.travel_period_from || null,
        travel_period_to: edit.travel_period_to || null,
        rbd: edit.rbd?.toUpperCase() || null,
        account_code_fares_only: !!edit.account_code_fares_only,
        priority: Number(edit.priority || 50),
        active: edit.active !== false,
        note: edit.note || null,
      });
      toast.success(`Saved ${edit.airline_code.toUpperCase()} · ${edit.code.toUpperCase()}`);
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm(`Delete ${id}?`)) return;
    try { await api.delete(`/admin/airline-account-codes/${encodeURIComponent(id)}`); toast.success("Deleted"); load(); }
    catch (_e) { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-airline-account-codes">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[var(--brand-accent)] flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[var(--brand-primary)]" /> Airline Account Codes
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl leading-relaxed">
            Negotiated <strong>private-fare unlock codes</strong> shipped to Travelport (1G) on every search → price → book call. Active when today &amp; the requested travel date both fall inside the configured windows. Matching engine picks the most specific record (RBD-filtered &gt; blanket, narrower window &gt; broader).
          </p>
        </div>
        <Button data-testid="acct-code-add-btn" onClick={() => setEdit({ priority: 50, active: true, account_code_fares_only: false })} className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
          <Plus className="w-4 h-4 mr-1" /> Add account code
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Airline</TableHead>
            <TableHead>Account Code</TableHead>
            <TableHead>Contract</TableHead>
            <TableHead>PCC</TableHead>
            <TableHead>Booking Period</TableHead>
            <TableHead>Travel Period</TableHead>
            <TableHead>RBD</TableHead>
            <TableHead>Only</TableHead>
            <TableHead>Prio</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`acc-row-${r.id}`}>
                <TableCell className="font-mono font-bold text-[var(--brand-accent)]">{r.airline_code}</TableCell>
                <TableCell className="font-mono font-semibold">{r.code}</TableCell>
                <TableCell className="font-mono text-xs">{r.contract_code || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.pseudo_city_code || "—"}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{(r.booking_period_from || "—") + " → " + (r.booking_period_to || "—")}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{(r.travel_period_from || "—") + " → " + (r.travel_period_to || "—")}</TableCell>
                <TableCell className="font-mono text-xs">{r.rbd || "—"}</TableCell>
                <TableCell>{r.account_code_fares_only ? <Badge className="bg-emerald-50 text-emerald-700 border-0">Yes</Badge> : "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.priority}</TableCell>
                <TableCell><Badge className={`${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-600"} border-0`}>{r.active ? r.status : "Disabled"}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`acc-edit-${r.id}`} size="sm" variant="ghost" onClick={() => setEdit({ ...r })}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button data-testid={`acc-del-${r.id}`} size="sm" variant="ghost" onClick={() => del(r.id)} className="text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={11} className="text-center py-10 text-slate-400">No account codes configured. Click <strong className="text-[var(--brand-accent)]">Add account code</strong> to register your first negotiated deal.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent data-testid="acct-code-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{edit?.id ? `Edit ${edit.airline_code} · ${edit.code}` : "Add account code"}</DialogTitle>
            <DialogDescription>Goes into <code>AccountCodes / ContractCodes</code> on every Travelport call for the matched window.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Airline</Label><Input data-testid="acc-airline" value={edit?.airline_code || ""} onChange={(e) => setEdit({ ...edit, airline_code: e.target.value.toUpperCase().slice(0, 3) })} className="font-mono uppercase" placeholder="EK / MS / SV" /></div>
            <div><Label>Account Code</Label><Input data-testid="acc-code" value={edit?.code || ""} onChange={(e) => setEdit({ ...edit, code: e.target.value.toUpperCase() })} className="font-mono uppercase" placeholder="EKBSP2026" /></div>
            <div><Label>Contract Code (opt)</Label><Input data-testid="acc-contract" value={edit?.contract_code || ""} onChange={(e) => setEdit({ ...edit, contract_code: e.target.value.toUpperCase() })} className="font-mono uppercase" /></div>
            <div><Label>PseudoCityCode (opt)</Label><Input data-testid="acc-pcc" value={edit?.pseudo_city_code || ""} onChange={(e) => setEdit({ ...edit, pseudo_city_code: e.target.value.toUpperCase() })} className="font-mono uppercase" /></div>
            <div><Label>Booking Period From</Label><Input data-testid="acc-bp-from" type="date" value={edit?.booking_period_from || ""} onChange={(e) => setEdit({ ...edit, booking_period_from: e.target.value })} /></div>
            <div><Label>Booking Period To</Label><Input data-testid="acc-bp-to" type="date" value={edit?.booking_period_to || ""} onChange={(e) => setEdit({ ...edit, booking_period_to: e.target.value })} /></div>
            <div><Label>Travel Period From</Label><Input data-testid="acc-tp-from" type="date" value={edit?.travel_period_from || ""} onChange={(e) => setEdit({ ...edit, travel_period_from: e.target.value })} /></div>
            <div><Label>Travel Period To</Label><Input data-testid="acc-tp-to" type="date" value={edit?.travel_period_to || ""} onChange={(e) => setEdit({ ...edit, travel_period_to: e.target.value })} /></div>
            <div><Label>RBD (opt, comma-sep)</Label><Input data-testid="acc-rbd" value={edit?.rbd || ""} onChange={(e) => setEdit({ ...edit, rbd: e.target.value.toUpperCase() })} className="font-mono uppercase" placeholder="Y,B,M" /></div>
            <div><Label>Priority (1-100)</Label><Input data-testid="acc-prio" type="number" min={1} max={100} value={edit?.priority ?? 50} onChange={(e) => setEdit({ ...edit, priority: e.target.value })} /></div>
            <div className="flex items-center gap-2 col-span-2">
              <Switch data-testid="acc-only" checked={!!edit?.account_code_fares_only} onCheckedChange={(v) => setEdit({ ...edit, account_code_fares_only: v })} />
              <Label>Account-Code Fares Only (hide public fares for matched calls)</Label>
            </div>
            <div className="flex items-center gap-2 col-span-2">
              <Switch data-testid="acc-active" checked={edit?.active !== false} onCheckedChange={(v) => setEdit({ ...edit, active: v })} />
              <Label>Active</Label>
            </div>
            <div className="col-span-2"><Label>Note</Label><Input data-testid="acc-note" value={edit?.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} placeholder="e.g. EK BSP-EG contract 2026-Q1" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            <Button data-testid="acc-save-btn" onClick={save} disabled={busy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
